import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Camera, Users2, LogIn, LogOut } from 'lucide-react';

export default function PhotoMonitoringPage() {
  const { user } = useAuth();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [viewer, setViewer] = useState({ open: false, src: '', title: '' });

  const isAllowed = ['super_admin', 'management'].includes(user?.system_role);

  const fetchData = async () => {
    try {
      const [empRes, attRes, meetRes] = await Promise.all([
        api.get('/employees'),
        api.get('/attendance', { params: { date, include_photos: true } }),
        api.get('/meeting-photos'),
      ]);
      setEmployees(empRes.data);
      setRecords(attRes.data);
      // Filter meetings to just the selected day
      const dayMeetings = meetRes.data.filter(m => (m.date || '').startsWith(date));
      setMeetings(dayMeetings);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { if (isAllowed) fetchData(); /* eslint-disable-next-line */ }, [date]);

  if (!isAllowed) {
    return <div className="p-6 text-neutral-800">Access denied. This page is for Super Admin and HR only.</div>;
  }

  const empById = Object.fromEntries(employees.map(e => [e.id, e]));

  const entryPhotos = records.filter(r => r.photo_base64);
  const exitPhotos = records.filter(r => r.exit_photo_base64);

  const openPhoto = (src, title) => {
    const full = src.startsWith('data:') ? src : `data:image/jpeg;base64,${src}`;
    setViewer({ open: true, src: full, title });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="photo-monitoring-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Photo Monitoring</h1>
          <p className="text-neutral-700 text-sm mt-1">Verify authenticity of daily punch-in / punch-out selfies and meeting photos.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-neutral-700">Date</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="w-auto rounded-sm" data-testid="photo-date-filter" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Punch-in Selfies', value: entryPhotos.length, icon: LogIn, color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
          { label: 'Punch-out Selfies', value: exitPhotos.length, icon: LogOut, color: 'bg-blue-50 text-blue-700 border-blue-100' },
          { label: 'Meeting Photos', value: meetings.length, icon: Users2, color: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
          { label: 'Missing Photos', value: records.filter(r => r.status === 'present' && !r.photo_base64 && empById[r.employee_id]?.system_role !== 'super_admin').length, icon: Camera, color: 'bg-amber-50 text-amber-700 border-amber-100' },
        ].map((s, i) => (
          <div key={i} className={`border rounded-md p-4 ${s.color}`}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{s.label}</p>
              <s.icon className="h-4 w-4 opacity-60" />
            </div>
            <p className="text-3xl font-bold tracking-tight mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="entry">
        <TabsList>
          <TabsTrigger value="entry" data-testid="tab-entry-photos">Punch-in</TabsTrigger>
          <TabsTrigger value="exit" data-testid="tab-exit-photos">Punch-out</TabsTrigger>
          <TabsTrigger value="meetings" data-testid="tab-meeting-photos">Meetings</TabsTrigger>
        </TabsList>

        <TabsContent value="entry" className="mt-4">
          <PhotoGrid items={entryPhotos.map(r => ({
            id: r.id, photo: r.photo_base64,
            name: empById[r.employee_id]?.name || 'Unknown',
            subtitle: `${r.in_time || '—'} · ${empById[r.employee_id]?.branch_id ? '' : ''}${r.status}`,
          }))} onOpen={(i) => openPhoto(i.photo, `${i.name} · Punch-in ${date}`)} />
        </TabsContent>

        <TabsContent value="exit" className="mt-4">
          <PhotoGrid items={exitPhotos.map(r => ({
            id: r.id, photo: r.exit_photo_base64,
            name: empById[r.employee_id]?.name || 'Unknown',
            subtitle: `${r.out_time || '—'} · ${r.status}`,
          }))} onOpen={(i) => openPhoto(i.photo, `${i.name} · Punch-out ${date}`)} />
        </TabsContent>

        <TabsContent value="meetings" className="mt-4">
          <PhotoGrid items={meetings.map(m => ({
            id: m.id, photo: m.photo_base64,
            name: m.title || 'Meeting',
            subtitle: m.date,
          }))} onOpen={(i) => openPhoto(i.photo, i.name)} />
        </TabsContent>
      </Tabs>

      {/* Viewer */}
      <Dialog open={viewer.open} onOpenChange={(o) => !o && setViewer({ open: false, src: '', title: '' })}>
        <DialogContent className="max-w-2xl" data-testid="photo-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{viewer.title}</DialogTitle>
            <DialogDescription>Full-size verification view</DialogDescription>
          </DialogHeader>
          {viewer.src && <img src={viewer.src} alt={viewer.title} className="w-full rounded-md border border-neutral-200" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoGrid({ items, onOpen }) {
  if (items.length === 0) {
    return (
      <Card className="border border-neutral-200 rounded-md">
        <CardContent className="py-14 text-center text-neutral-700">
          <Camera className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
          <p className="text-sm">No photos for this selection.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="photo-grid">
      {items.map(i => {
        if (!i.photo) return null;
        const src = i.photo.startsWith('data:') ? i.photo : `data:image/jpeg;base64,${i.photo}`;
        return (
          <button key={i.id} onClick={() => onOpen(i)} className="group relative overflow-hidden rounded-md border border-neutral-200 hover:border-red-400 transition-all">
            <img src={src} alt={i.name} className="w-full aspect-square object-cover group-hover:scale-[1.03] transition-transform" />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-2">
              <p className="text-xs font-semibold text-white truncate">{i.name}</p>
              <p className="text-[10px] text-white/80 truncate">{i.subtitle}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
